import { AnalyticsConstants } from "../../presentation/assets/constants/analytics.constants";
import { CharactersFeature } from "../characters/charactersFeature";
import { User } from "../models/User";
import { UserFeature } from "../user/userFeature";
import { AnalyticsFeature } from "./analyticsFeature";

interface AnalyticsUserProperties {
  playMode: string;
  selectedCharacter: string | null;
  characterCount: number | null;
}

/**
 * Keeps the Firebase user properties in sync with how the user plays.
 *
 * These are what make every OTHER event in the property segmentable — swipes,
 * purchases, sessions and retention can all be cut by `play_mode` without
 * carrying a parameter on each one, and GA4 audiences can be built from them.
 */
export class AnalyticsUserPropertiesFeature {
  private static unsubscribeUser: (() => void) | null = null;
  private static lastSent: Partial<AnalyticsUserProperties> = {};
  private static observedUserId: string | null = null;
  /** Sorted partnership ids of the last emission; null before the first. */
  private static lastPartnershipsKey: string | null = null;

  public static derivePlayMode(user: User): string {
    const { modes } = AnalyticsConstants.values;
    // Order matters: connecting to a character also sets isPlayingAlone = true,
    // so checking solo first would report every character player as solo.
    if (user.selectedCharacter) {
      return modes.character;
    }
    return user.isPlayingAlone ? modes.solo : modes.partner;
  }

  public static startForAuthenticatedUser(userId: string): void {
    // Only the subscription is torn down here — clearing the properties would
    // blank them for the moment between a restart and the first emission.
    this.teardownObserver();
    this.lastSent = {};
    this.observedUserId = userId;

    try {
      this.unsubscribeUser = UserFeature.observeCurrentUser(
        (user) => {
          const selectedCharacter = user.selectedCharacter ?? null;
          const selectionChanged =
            this.lastSent.selectedCharacter !== undefined &&
            this.lastSent.selectedCharacter !== selectedCharacter;

          this.publish({
            playMode: this.derivePlayMode(user),
            selectedCharacter,
          });

          // Not derivable from the observed user: `validPartnershipsIdentifiers`
          // merges every partnership category, so the count needs its own read.
          // It is only issued once the profile has emitted — a brand-new
          // account has an auth user before onboarding writes the profile,
          // and reading the partnerships before that is a guaranteed miss.
          // Re-read whenever the partnership set or the selection moves.
          const partnershipsKey = [...user.validPartnershipsIdentifiers]
            .sort()
            .join(",");
          const partnershipsChanged =
            this.lastPartnershipsKey !== partnershipsKey;
          this.lastPartnershipsKey = partnershipsKey;
          if (partnershipsChanged || selectionChanged) {
            this.refreshCharacterCount(userId);
          }
        },
        () => undefined,
      );
    } catch {
      // No authenticated user yet — nothing to segment by.
      this.observedUserId = null;
      return;
    }
  }

  private static teardownObserver(): void {
    this.unsubscribeUser?.();
    this.unsubscribeUser = null;
    this.observedUserId = null;
    this.lastPartnershipsKey = null;
  }

  public static stopForAuthenticatedUser(): void {
    this.teardownObserver();
    this.lastSent = {};
    AnalyticsFeature.trackUserProperty(
      AnalyticsConstants.userProperties.playMode,
      null,
    );
    AnalyticsFeature.trackUserProperty(
      AnalyticsConstants.userProperties.selectedCharacter,
      null,
    );
    AnalyticsFeature.trackUserProperty(
      AnalyticsConstants.userProperties.characterCount,
      null,
    );
  }

  private static refreshCharacterCount(userId: string): void {
    CharactersFeature.loadConnectedCharacterIds(userId)
      .then((ids) => {
        // A sign-out (or a re-login as someone else) may have landed while the
        // read was in flight.
        if (this.observedUserId === userId) {
          this.publish({ characterCount: ids.size });
        }
      })
      .catch(() => undefined);
  }

  /** Sends only what changed — the user observer re-emits on every write. */
  private static publish(next: Partial<AnalyticsUserProperties>): void {
    const { userProperties } = AnalyticsConstants;

    if (
      next.playMode !== undefined &&
      next.playMode !== this.lastSent.playMode
    ) {
      this.lastSent.playMode = next.playMode;
      AnalyticsFeature.trackUserProperty(
        userProperties.playMode,
        next.playMode,
      );
    }

    if (
      next.selectedCharacter !== undefined &&
      next.selectedCharacter !== this.lastSent.selectedCharacter
    ) {
      this.lastSent.selectedCharacter = next.selectedCharacter;
      AnalyticsFeature.trackUserProperty(
        userProperties.selectedCharacter,
        next.selectedCharacter,
      );
    }

    if (
      next.characterCount !== undefined &&
      next.characterCount !== this.lastSent.characterCount
    ) {
      this.lastSent.characterCount = next.characterCount;
      AnalyticsFeature.trackUserProperty(
        userProperties.characterCount,
        next.characterCount,
      );
    }
  }
}
